import { Outlet, useParams } from 'react-router-dom';
// material
import { Card, CardContent, CardMedia, Fade, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import { BookingRouteParams, MerchantOutletContext } from '@/@type/router/booking';
import LoadingComponent from '@/components/LoadingComponent';
import { useFetchMerchant } from '@/components/public/booking/components/useFetchMerchant';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

// ----------------------------------------------------------------------

const HeaderStyle = styled('header')(({ theme }) => ({
    top: 0,
    left: 0,
    lineHeight: 0,
    width: '100%',
    padding: theme.spacing(0),
    zIndex: 10,
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3, 3, 0)
    }
}));
// ----------------------------------------------------------------------

export default function MerchantOnlyLayout(): JSX.Element {
    const { merchantId } = useParams<BookingRouteParams>();
    const { showSnackbar } = useSnackbarHelper();
    const { merchant, bookingSettings, fetchMerchantLoading, fetchMerchantError } = useFetchMerchant(merchantId || '');
    const [logoImages, setLogoImages] = useState<string[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [context, setContext] = useState<MerchantOutletContext>({ merchantId: merchantId || '' });

    useEffect(() => {
        if (merchantId) {
            setContext((prev) => ({ ...prev, merchantId }));
        }
    }, [merchantId]);

useEffect(() => {
    if (merchant) {
        setContext((prev) => ({ ...prev, merchant }));
    }
    if (merchant?.logoImages && merchant.logoImages.length > 0) {
        const filteredImages = merchant.logoImages.filter((image): image is string => 
            image !== null && image !== undefined && image !== ''
        );
        setLogoImages(filteredImages);
        setCurrentImageIndex(0);
    }
}, [merchant]);

    useEffect(() => {
        if (bookingSettings) {
            setContext((prev) => ({ ...prev, bookingSettings }));
        }
    }, [bookingSettings]);

    useEffect(() => {
        if (fetchMerchantError) {
            showSnackbar('Merchant not found', 'error');
        }
    }, [fetchMerchantError, showSnackbar]);
    useEffect(() => {
        if (currentImageIndex >= logoImages.length && logoImages.length > 0) {
            setCurrentImageIndex(0);
        }
    }, [logoImages, currentImageIndex]);

    useEffect(() => {
        if (logoImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentImageIndex((prevIndex) => (prevIndex + 1) % logoImages.length);
            }, 3000);
            return (): void => clearInterval(interval);
        }
    }, [logoImages.length]);

    return (
        <>
            <HeaderStyle>
                {fetchMerchantLoading && <LoadingComponent />}
                {!fetchMerchantLoading && !fetchMerchantError && merchant && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            maxWidth: { xs: '100%', sm: 800 },
                            mx: { xs: 0, sm: 'auto' },
                            width: '100%',
                            px: { xs: 0, sm: 2 }
                        }}
                    >
                        <Card
                            sx={{
                                width: '100%',
                                borderRadius: { xs: 0, sm: 1 },
                                boxShadow: 'none'
                            }}
                        >
                            <Box sx={{ height: 150, position: 'relative', overflow: 'hidden' }}>
                                {logoImages.map((image, index) => (
                                    <Fade
                                        key={index}
                                        in={index === currentImageIndex}
                                        timeout={800}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    >
                                        <CardMedia sx={{ height: '100%', width: '100%' }} image={image} title="merchant logo" />
                                    </Fade>
                                ))}
                            </Box>
                            <CardContent
                                sx={{
                                    paddingX: 4,
                                    paddingTop: 1,
                                    '&:last-child': {
                                        paddingBottom: 1
                                    }
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {merchant.name}
                                </Typography>
                                {merchant.altName && (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        {merchant.altName}
                                    </Typography>
                                )}
                                {merchant.phoneNumber && (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        <Link href={`tel:${merchant?.phoneNumber}`} sx={{ color: 'inherit', textDecoration: 'underline' }}>
                                            📞 {merchant?.phoneNumber}
                                        </Link>
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    📍 {merchant.address}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </HeaderStyle>

            <Box>
                <Outlet context={context} />
            </Box>
        </>
    );
}
